import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { personDisplayName, trainerPersonalChatTitle } from "../../../utils/clientAppUtils";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import cs from "./chat.module.css";
import { useToast } from "../../ui";
import ChatMessageActionSheet from "./ChatMessageActionSheet";
import ReactionBar from "./ReactionBar";
import { applyOptimisticReaction } from "../../../constants/reactions";

function chatSenderLabel(name) {
  if (!name || name === "Вы") return name;
  return personDisplayName(name) || name;
}

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatDayLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return "Сегодня";
  if (d.toDateString() === yesterday.toDateString()) return "Вчера";
  return d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "long",
    year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

export function initials(name) {
  const parts = String(name || "?")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function ChatAvatar({
  photoUrl,
  name,
  className,
  imgClassName,
  club,
  group,
}) {
  const src = mediaUrl(photoUrl);
  if (club) {
    return (
      <span className={className || cs.chatInboxAvatarClub} aria-hidden>
        🏟
      </span>
    );
  }
  if (group) {
    return (
      <span className={className || cs.chatInboxAvatar} aria-hidden>
        👥
      </span>
    );
  }
  if (src) {
    const img = (
      <img
        src={src}
        alt=""
        className={imgClassName || cs.chatAvatarImg}
        loading="lazy"
      />
    );
    if (className) {
      return (
        <span className={className} aria-hidden>
          {img}
        </span>
      );
    }
    return img;
  }
  return (
    <span className={className || cs.chatAvatar} aria-hidden>
      {initials(name)}
    </span>
  );
}

function chatSubtitle(meta, mode) {
  if (!meta) return "";
  if (mode === "staff") {
    const groups = (meta.groups || []).map((g) => g.name).filter(Boolean);
    if (groups.length) return groups.join(", ");
    return "онлайн в приложении";
  }
  const trainers = meta.trainers || [];
  if (trainers.length) {
    return trainers.length === 1
      ? trainerPersonalChatTitle(trainers)
      : "Администрация и тренеры";
  }
  return "Сообщения команде клуба";
}

function groupMessagesByDay(messages) {
  const groups = [];
  let currentDay = null;
  for (const msg of messages) {
    const day = formatDayLabel(msg.created_at);
    if (day !== currentDay) {
      currentDay = day;
      groups.push({ type: "day", key: `day-${day}-${msg.id}`, label: day });
    }
    groups.push({ type: "msg", key: msg.id, msg });
  }
  return groups;
}

const POLL_MS = 1500;
const SYNC_READ_EVERY = 2;
const MESSAGE_EDIT_WINDOW_MS = 48 * 60 * 60 * 1000;

function mergeChatMessages(prev, incoming) {
  if (!incoming?.length) return prev;
  const map = new Map(
    prev.filter((m) => !m.pending).map((m) => [String(m.id), m]),
  );
  for (const m of incoming) {
    const key = String(m.id);
    const existing = map.get(key);
    map.set(key, existing ? { ...existing, ...m } : m);
  }
  return [...map.values()].sort((a, b) => {
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    if (ta !== tb) return ta - tb;
    return String(a.id).localeCompare(String(b.id));
  });
}

function emitChatActivity() {
  window.dispatchEvent(new CustomEvent("baysport:chat-activity"));
}

function messageEditable(message) {
  if (!message || message.is_deleted || message.attachment_url) return false;
  const created = new Date(message.created_at);
  if (Number.isNaN(created.getTime())) return false;
  return Date.now() - created.getTime() <= MESSAGE_EDIT_WINDOW_MS;
}

function AttachIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ReadTicks({ read }) {
  return (
    <span
      className={`${cs.chatReadTicks} ${read ? cs.chatReadTicksRead : ""}`}
      title={read ? "Прочитано" : "Доставлено"}
      aria-label={read ? "Прочитано" : "Доставлено"}
    >
      {read ? "✓✓" : "✓"}
    </span>
  );
}

function ChatAttachment({ url, type }) {
  const src = mediaUrl(url);
  if (!src) return null;
  if (type === "video") {
    return (
      <video
        className={cs.chatBubbleVideo}
        src={src}
        controls
        playsInline
        preload="metadata"
      />
    );
  }
  return (
    <a
      className={cs.chatBubbleImageLink}
      href={src}
      target="_blank"
      rel="noopener noreferrer"
    >
      <img className={cs.chatBubbleImage} src={src} alt="Фото" loading="lazy" />
    </a>
  );
}

function SendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12l14-7-4 14-3-5-7-2z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChatEmptyIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ChatThread({
  mode = "client",
  threadType = "direct",
  patientId,
  peerUserId,
  groupId,
  athleteQueryParam = {},
  variant = "page",
  onMeta,
  initialDraft = "",
}) {
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const isMobile = useIsMobile(768);

  const athleteId = athleteQueryParam?.athlete_id ?? "";
  const chatKey =
    threadType === "club"
      ? `${mode}:club`
      : threadType === "group"
        ? `${mode}:group:${groupId || ""}`
        : threadType === "member"
          ? `${mode}:member:${peerUserId || ""}`
          : mode === "staff"
            ? `staff:${patientId}`
            : `client:${athleteId || "self"}`;

  const [messages, setMessages] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [actionAnchor, setActionAnchor] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageBusy, setMessageBusy] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const pollCountRef = useRef(0);
  const inFlightRef = useRef(false);
  const prevCountRef = useRef(0);
  const onMetaRef = useRef(onMeta);
  onMetaRef.current = onMeta;
  const messagesRef = useRef([]);
  messagesRef.current = messages;

  const fetchChat = useCallback(
    async ({ silent = false, incremental = false, merge = false } = {}) => {
      if (inFlightRef.current) return null;
      if (threadType === "direct" && mode === "staff" && !patientId) return null;
      if (threadType === "member" && !peerUserId) return null;
      if (threadType === "group" && !groupId) return null;

      inFlightRef.current = true;
      try {
        const lastMsg = incremental
          ? messagesRef.current[messagesRef.current.length - 1]
          : null;
        const since =
          incremental && lastMsg?.created_at ? lastMsg.created_at : undefined;
        const clientParams = athleteId ? { athlete_id: athleteId } : {};
        const readParams = { mark_read: "1" };
        if (since) clientParams.since = since;
        Object.assign(clientParams, readParams);

        let data;
        if (threadType === "club") {
          data =
            mode === "staff"
              ? await clinicAPI.getClubChat(since ? { since } : {})
              : await clinicAPI.getMyClubChat(since ? { since } : {});
        } else if (threadType === "group") {
          const groupParams = { ...clientParams };
          if (since) groupParams.since = since;
          data =
            mode === "staff"
              ? await clinicAPI.getStaffGroupChat(groupId, groupParams)
              : await clinicAPI.getMyGroupChat(groupId, groupParams);
        } else if (threadType === "member") {
          data =
            mode === "staff"
              ? await clinicAPI.getStaffMemberChat(peerUserId, clientParams)
              : await clinicAPI.getMyMemberChat(peerUserId, clientParams);
        } else {
          const staffParams = since ? { since, ...readParams } : readParams;
          data =
            mode === "staff"
              ? await clinicAPI.getStaffClientChat(patientId, staffParams)
              : await clinicAPI.getMyChat(clientParams);
        }

        const incoming = data.messages || [];
        let hasNewMessages = false;
        if (incoming.length) {
          const prevIds = new Set(messagesRef.current.map((m) => String(m.id)));
          hasNewMessages = incoming.some((m) => !prevIds.has(String(m.id)));
        }
        if (incremental && since && incoming.length) {
          setMessages((prev) => {
            const ids = new Set(prev.map((m) => m.id));
            const merged = [...prev];
            for (const m of incoming) {
              if (!ids.has(m.id)) merged.push(m);
            }
            return merged;
          });
        } else if (merge && incoming.length) {
          setMessages((prev) => mergeChatMessages(prev, incoming));
        } else if (!incremental) {
          setMessages(incoming);
        }
        if (hasNewMessages && silent) {
          emitChatActivity();
        }
        if (!silent) {
          emitChatActivity();
        }
        if (data.meta) {
          setMeta(data.meta);
          onMetaRef.current?.(data.meta);
        }
        return data;
      } catch (e) {
        if (!silent) {
          toastRef.current.error(
            e.response?.data?.error || "Не удалось загрузить чат",
          );
        }
        return null;
      } finally {
        inFlightRef.current = false;
        if (!silent) setLoading(false);
      }
    },
    [mode, threadType, patientId, peerUserId, groupId, athleteId],
  );

  const fetchChatRef = useRef(fetchChat);
  fetchChatRef.current = fetchChat;

  useEffect(() => {
    pollCountRef.current = 0;
    setLoading(true);
    setMessages([]);
    setMeta(null);
    setText("");
    setActionMessage(null);
    setEditingMessage(null);
    fetchChatRef.current({ silent: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when chat identity changes
  }, [chatKey]);

  useEffect(() => {
    if (!initialDraft) return;
    setText(initialDraft);
  }, [chatKey, initialDraft]);

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState !== "visible") return;
      pollCountRef.current += 1;
      const hasMessages = messagesRef.current.length > 0;
      const needFullSync =
        !hasMessages ||
        threadType === "member" ||
        (threadType === "direct" &&
          pollCountRef.current % SYNC_READ_EVERY === 0);
      if (needFullSync) {
        fetchChatRef.current({ silent: true, incremental: false, merge: true });
      } else {
        fetchChatRef.current({ silent: true, incremental: true });
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    const id = window.setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll on stable chat identity
  }, [chatKey, threadType]);

  useEffect(() => {
    if (messages.length <= prevCountRef.current) {
      prevCountRef.current = messages.length;
      return;
    }
    prevCountRef.current = messages.length;
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const grouped = useMemo(() => groupMessagesByDay(messages), [messages]);

  const postMessage = async (payload) => {
    if (threadType === "club") {
      return mode === "staff"
        ? clinicAPI.sendClubChat(payload)
        : clinicAPI.sendMyClubChat(payload);
    }
    if (threadType === "group") {
      return mode === "staff"
        ? clinicAPI.sendStaffGroupChat(groupId, payload)
        : clinicAPI.sendMyGroupChat(
            groupId,
            payload,
            athleteId ? { athlete_id: athleteId } : {},
          );
    }
    if (threadType === "member") {
      return mode === "staff"
        ? clinicAPI.sendStaffMemberChat(peerUserId, payload)
        : clinicAPI.sendMyMemberChat(
            peerUserId,
            payload,
            athleteId ? { athlete_id: athleteId } : {},
          );
    }
    if (mode === "staff") {
      return clinicAPI.sendStaffClientChat(patientId, payload);
    }
    return clinicAPI.sendMyChat(
      payload,
      athleteId ? { athlete_id: athleteId } : {},
    );
  };

  const patchMessageApi = (messageId, data) =>
    mode === "staff"
      ? clinicAPI.updateStaffChatMessage(messageId, data)
      : clinicAPI.updateMyChatMessage(messageId, data);

  const deleteMessageApi = (messageId) =>
    mode === "staff"
      ? clinicAPI.deleteStaffChatMessage(messageId)
      : clinicAPI.deleteMyChatMessage(messageId);

  const reactMessageApi = (messageId, emoji) =>
    mode === "staff"
      ? clinicAPI.toggleStaffChatMessageReaction(messageId, emoji)
      : clinicAPI.toggleMyChatMessageReaction(messageId, emoji);

  const applyMessageUpdate = (updated) => {
    if (!updated?.id) return;
    setMessages((prev) =>
      prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)),
    );
  };

  const reactingRef = useRef(new Set());

  const toggleMessageReaction = async (message, emoji) => {
    if (!message?.id || message.is_deleted || reactingRef.current.has(message.id)) {
      return;
    }
    const snapshot = {
      reactions: { ...(message.reactions || {}) },
      my_reaction: message.my_reaction || null,
    };
    const optimistic = applyOptimisticReaction(message, emoji);
    reactingRef.current.add(message.id);
    applyMessageUpdate({ id: message.id, ...optimistic });
    setActionMessage(null);
    setActionAnchor(null);
    try {
      const data = await reactMessageApi(message.id, emoji);
      if (data.message) {
        applyMessageUpdate(data.message);
      } else {
        applyMessageUpdate({
          id: message.id,
          reactions: data.reactions || {},
          my_reaction: data.my_reaction ?? null,
        });
      }
    } catch (err) {
      applyMessageUpdate({ id: message.id, ...snapshot });
      toastRef.current.error(
        err.response?.data?.error || "Не удалось поставить реакцию",
      );
    } finally {
      reactingRef.current.delete(message.id);
    }
  };

  const closeActionSheet = () => {
    if (!messageBusy) {
      setActionMessage(null);
      setActionAnchor(null);
    }
  };

  const openMessageActions = (message, event) => {
    setActionMessage(message);
    setActionAnchor(event?.currentTarget || null);
  };

  const startEditMessage = (message) => {
    setActionMessage(null);
    setActionAnchor(null);
    setEditingMessage(message);
    setText(message.body || "");
    window.setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.style.height = "auto";
        inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
      }
    }, 0);
  };

  const cancelEditMessage = () => {
    setEditingMessage(null);
    setText("");
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
  };

  const confirmDeleteMessage = async (message) => {
    if (!message || messageBusy) return;
    setMessageBusy(true);
    try {
      const data = await deleteMessageApi(message.id);
      if (data.message) {
        applyMessageUpdate(data.message);
      }
      setActionMessage(null);
      setActionAnchor(null);
      toastRef.current.success("Сообщение удалено");
    } catch (err) {
      toastRef.current.error(err.response?.data?.error || "Не удалось удалить");
    } finally {
      setMessageBusy(false);
    }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body || sending || uploading || messageBusy) return;

    if (editingMessage) {
      setSending(true);
      try {
        const data = await patchMessageApi(editingMessage.id, { body });
        if (data.message) {
          applyMessageUpdate(data.message);
        }
        cancelEditMessage();
        toastRef.current.success("Сообщение изменено");
      } catch (err) {
        toastRef.current.error(err.response?.data?.error || "Не удалось сохранить");
      } finally {
        setSending(false);
      }
      return;
    }

    setSending(true);
    const optimisticId = `pending-${Date.now()}`;
    const optimisticMessage = {
      id: optimisticId,
      body,
      is_mine: true,
      pending: true,
      sender_name: "Вы",
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticMessage]);
    setText("");
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
    try {
      const data = await postMessage({ body });
      if (data.message) {
        setMessages((prev) =>
          prev.map((m) => (m.id === optimisticId ? data.message : m)),
        );
      } else {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      }
      emitChatActivity();
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
      setText(body);
      toastRef.current.error(err.response?.data?.error || "Не удалось отправить");
    } finally {
      setSending(false);
    }
  };

  const onAttachPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || sending || uploading) return;

    setUploading(true);
    try {
      const uploaded =
        mode === "staff"
          ? await clinicAPI.uploadStaffChatMedia(file)
          : await clinicAPI.uploadMyChatMedia(file);

      const caption = text.trim();
      const data = await postMessage({
        body: caption,
        attachment_url: uploaded.url,
        attachment_type: uploaded.attachment_type,
      });

      if (data.message) {
        setMessages((prev) => [...prev, data.message]);
      }
      setText("");
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
      }
      emitChatActivity();
    } catch (err) {
      toastRef.current.error(
        err.response?.data?.error || "Не удалось прикрепить файл",
      );
    } finally {
      setUploading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onInput = (e) => {
    setText(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
  };

  const wrapClass =
    variant === "sheet"
      ? `${cs.chatWrap} ${cs.chatWrapSheet}`
      : cs.chatWrap;

  if (loading) {
    return (
      <div className={wrapClass}>
        <div className={cs.chatMessages}>
          <div className={cs.chatLoading}>
            <div className={cs.chatLoadingDots} aria-hidden>
              <span />
              <span />
              <span />
            </div>
            Загрузка чата…
          </div>
        </div>
        <form className={cs.chatComposer} onSubmit={(e) => e.preventDefault()} aria-hidden>
          <textarea className={cs.chatInput} rows={1} placeholder="Сообщение" disabled />
          <button type="button" className={cs.chatSendBtn} disabled aria-hidden>
            ↑
          </button>
        </form>
      </div>
    );
  }

  const emptyHint =
    threadType === "club"
      ? "Напишите всем участникам клуба — сообщение увидят в приложении."
      : threadType === "group"
        ? "Общий чат вашей тренировочной группы — тренер и участники увидят сообщение здесь."
        : threadType === "member"
        ? "Личная переписка с участником клуба."
        : mode === "staff"
          ? "Клиент получит push и увидит сообщение в приложении."
          : "Напишите тренеру или администратору — ответ придёт сюда.";

  return (
    <div className={wrapClass}>
      <div
        className={`${cs.chatMessages} ${
          messages.length === 0 ? cs.chatMessagesEmpty : ""
        }`}
      >
        {messages.length === 0 ? (
          <div className={cs.chatEmpty}>
            <div className={cs.chatEmptyIcon}>
              <ChatEmptyIcon />
            </div>
            <p className={cs.chatEmptyTitle}>Начните переписку</p>
            <p className={cs.chatEmptyHint}>{emptyHint}</p>
          </div>
        ) : (
          grouped.map((item) => {
            if (item.type === "day") {
              return (
                <div key={item.key} className={cs.chatDaySep}>
                  {item.label}
                </div>
              );
            }
            const m = item.msg;
            const mine = m.is_mine;
            const deleted = Boolean(m.is_deleted);
            const hasMedia = Boolean(m.attachment_url) && !deleted;
            const canManage = mine && !deleted;
            return (
              <div
                key={item.key}
                className={`${cs.chatRow} ${mine ? cs.chatRowMine : cs.chatRowTheirs}`}
              >
                {!mine ? (
                  <ChatAvatar
                    photoUrl={m.sender_photo_url}
                    name={chatSenderLabel(m.sender_name)}
                    className={cs.chatAvatar}
                    imgClassName={cs.chatAvatarImg}
                  />
                ) : null}
                <div className={cs.chatBubbleCol}>
                  <div
                    className={`${cs.chatBubble} ${
                      mine ? cs.chatBubbleMine : cs.chatBubbleTheirs
                    } ${hasMedia ? cs.chatBubbleHasMedia : ""} ${
                      deleted ? cs.chatBubbleDeleted : ""
                    } ${canManage ? cs.chatBubbleManageable : ""} ${
                      m.pending ? cs.chatBubblePending : ""
                    }`}
                    onClick={
                      canManage
                        ? (e) => openMessageActions(m, e)
                        : undefined
                    }
                    onKeyDown={
                      canManage && isMobile
                        ? (e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openMessageActions(m, e);
                            }
                          }
                        : undefined
                    }
                    role={canManage && isMobile ? "button" : undefined}
                    tabIndex={canManage && isMobile ? 0 : undefined}
                    aria-label={canManage && isMobile ? "Действия с сообщением" : undefined}
                  >
                    {canManage && !isMobile ? (
                      <button
                        type="button"
                        className={cs.chatBubbleMenuBtn}
                        aria-label="Действия с сообщением"
                        aria-expanded={actionMessage?.id === m.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          openMessageActions(m, e);
                        }}
                      >
                        ⋯
                      </button>
                    ) : null}
                    {!mine && (mode === "client" || threadType === "club" || threadType === "group" || threadType === "member") ? (
                      <span className={cs.chatBubbleName}>{chatSenderLabel(m.sender_name)}</span>
                    ) : null}
                    {!mine && mode === "staff" && threadType === "direct" && m.sender_role === "client" ? (
                      <span className={cs.chatBubbleName}>{chatSenderLabel(m.sender_name)}</span>
                    ) : null}
                    {hasMedia ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        <ChatAttachment
                          url={m.attachment_url}
                          type={m.attachment_type}
                        />
                      </div>
                    ) : null}
                    {deleted ? (
                      <span className={cs.chatBubbleDeletedText}>Сообщение удалено</span>
                    ) : m.body ? (
                      <span className={cs.chatBubbleText}>{m.body}</span>
                    ) : null}
                    <span className={cs.chatBubbleFooter}>
                      {m.edited_at && !deleted ? (
                        <span className={cs.chatBubbleEdited}>изм.</span>
                      ) : null}
                      <span className={cs.chatBubbleTime}>
                        {formatTime(m.created_at)}
                      </span>
                      {mine &&
                      (threadType === "direct" || threadType === "member") &&
                      !deleted ? (
                        <ReadTicks read={Boolean(m.is_read)} />
                      ) : null}
                    </span>
                  </div>
                  {!deleted && !m.pending ? (
                    <div
                      className={`${cs.chatReactionWrap} ${
                        mine ? cs.chatReactionWrapMine : ""
                      }`}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <ReactionBar
                        reactions={m.reactions}
                        myReaction={m.my_reaction}
                        onReact={(emoji) => toggleMessageReaction(m, emoji)}
                        align={mine ? "end" : "start"}
                        size="sm"
                        className={cs.chatReactionBar}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      {editingMessage ? (
        <div className={cs.chatEditBanner}>
          <span>Редактирование сообщения</span>
          <button
            type="button"
            className={cs.chatEditCancel}
            onClick={cancelEditMessage}
            aria-label="Отменить редактирование"
          >
            ×
          </button>
        </div>
      ) : null}

      <form className={cs.chatComposer} onSubmit={send}>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
          className={cs.chatFileInput}
          onChange={onAttachPick}
          aria-hidden
          tabIndex={-1}
        />
        <button
          type="button"
          className={cs.chatAttachBtn}
          onClick={() => fileRef.current?.click()}
          disabled={sending || uploading || Boolean(editingMessage)}
          aria-label="Прикрепить фото или видео"
        >
          <AttachIcon />
        </button>
        <textarea
          ref={inputRef}
          className={cs.chatInput}
          rows={1}
          value={text}
          onChange={onInput}
          onKeyDown={onKeyDown}
          placeholder={
            uploading
              ? "Загрузка файла…"
              : editingMessage
                ? "Изменить сообщение…"
                : "Сообщение"
          }
          maxLength={4000}
          disabled={uploading}
          aria-label="Текст сообщения"
        />
        <button
          type="submit"
          className={cs.chatSendBtn}
          disabled={!text.trim() || sending || uploading || messageBusy}
          aria-label={editingMessage ? "Сохранить" : "Отправить"}
        >
          <SendIcon />
        </button>
      </form>

      <ChatMessageActionSheet
        message={actionMessage}
        open={Boolean(actionMessage)}
        busy={messageBusy}
        canEdit={messageEditable(actionMessage)}
        anchor={actionAnchor}
        onClose={closeActionSheet}
        onEdit={startEditMessage}
        onDelete={confirmDeleteMessage}
      />
    </div>
  );
}

export { chatSubtitle };
